import React, { useState, useMemo, useEffect, forwardRef, useRef } from "react";
import {
  Select,
  Box,
  TextField,
  Typography,
  InputAdornment,
  OutlinedInput,
  Button,
  Checkbox,
  IconButton,
  Switch,
} from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { useTranslation } from "react-i18next";

import { CompanyStatusLabel } from "../OrganizationalStructure/components/company-status-label";

const BinaryTreeCheckbox = forwardRef(function BinaryTreeCheckbox(props, ref) {
  const { visible, style, ...checkboxProps } = props;
  if (!visible) return null;

  return (
    <Checkbox
      {...checkboxProps}
      ref={ref}
      indeterminate={false}
      style={{ padding: 0, ...style }}
    />
  );
});

// 建立客製化的 TreeItem 元件
const CustomTreeItem = forwardRef(function CustomTreeItem(props, ref) {
  // 從 props 裡面把你的自定義屬性 type 解構出來
  const { nodeMap, slots, ...other } = props;
  const currentId = props.itemId;
  // 從 nodeMap 中找出這個節點的完整資料，取得 type
  const nodeData = nodeMap ? nodeMap[currentId] : null;
  const type = nodeData ? nodeData.type : null;

  // 根據你的資料 type 決定文字顏色 (顏色你可以隨意調整)
  const getColor = (nodeType) => {
    switch (nodeType) {
      case "Parent":
        return "#2b3384";
      case "Level_1_Company":
        return "#e6700f";
      case "Level_2_Company":
        return "#f28c38";
      case "Level_3_Company":
        return "#f6b179";
      case "Facilities":
        return "#4caf50";
      default:
        return "darkgray";
    }
  };

  return (
    <TreeItem
      ref={ref}
      {...other}
      label={<CompanyStatusLabel company={nodeData} name={props.label} />}
      slots={{ ...slots, checkbox: BinaryTreeCheckbox }}
      sx={{
        "& .MuiTreeItem-content .MuiTreeItem-label": {
          color: getColor(type), // 套用顏色
          // 總部文字加粗，如果有需要這個功能在開放
          // fontWeight: type === "Parent" ? "bold" : "normal",
        },
      }}
    />
  );
});

// 將樹狀結構扁平化，方便查找 parentId 與 childrenIds
const getNodeId = (item) => item?.id || item?.value;

const isVisibleTreeNode = (node) => {
  const nodeStatus = String(node?.nodeStatus || "").toLowerCase();
  return nodeStatus !== "hidden" && nodeStatus !== "readonly";
};

const isSelectableCompanyNode = (node) => {
  if (!node || !isVisibleTreeNode(node)) return false;

  const isCompany = !node.orgType || node.orgType === "COMPANY";
  const isEditable = !node.nodeStatus || node.nodeStatus === "editable";

  return isCompany && isEditable;
};

const buildTreeAndMap = (flatData) => {
  if (!flatData || !Array.isArray(flatData)) {
    return { tree: [], map: {} };
  }

  const map = {};
  const tree = [];

  // 第一步：初始化所有節點到 map 中，並將 Mui 需要的 id 和 label 映射好
  flatData.forEach((item) => {
    const id = getNodeId(item);
    // 防呆：確保一定要有 id
    if (!id) return;
    if (!isVisibleTreeNode(item)) return;

    map[id] = {
      ...item, // 保留原始資料
      id,
      label: item.companyName || item.orgName || item.label || "undefined", // RichTreeView 預設吃 label
      type: item.type || item.companyType,
      parentId: item.parentId,
      childrenIds: [], // 稍後填入
      children: [], // 稍後填入，給 RichTreeView 用
    };
  });

  // 第二步：建立父子關係
  flatData.forEach((item) => {
    if (!isVisibleTreeNode(item)) return;
    const node = map[getNodeId(item)];
    if (!node) return;
    if (item.parentId && map[item.parentId]) {
      // 如果有 parentId 且在 map 中找得到爸爸，就加進爸爸的 children 裡
      map[item.parentId].children.push(node);
      map[item.parentId].childrenIds.push(node.id);
    } else {
      // 如果沒有 parentId (或是 parentId 為 null)，代表它是最頂層的根節點
      tree.push(node);
    }
  });

  // 第三步：清理空陣列 (MUI TreeView 如果 children 是空陣列，有時會顯示展開箭頭但沒內容，最好移除或設為 undefined)
  Object.values(map).forEach((node) => {
    if (node.children.length === 0) {
      delete node.children;
    }
  });

  return { tree, map };
};

// 取得所有上層 (祖先)
const getAncestors = (id, map) => {
  const ancestors = [];
  let currentId = map[id]?.parentId;
  while (currentId) {
    ancestors.push(currentId);
    currentId = map[currentId]?.parentId;
  }
  return ancestors;
};

// 取得所有下層 (子孫)
const getDescendants = (id, map) => {
  let descendants = [];
  const children = map[id]?.childrenIds || [];
  children.forEach((childId) => {
    descendants.push(childId);
    descendants = descendants.concat(getDescendants(childId, map)); // 遞迴往下找
  });
  return descendants;
};

// 核心邏輯：遞迴過濾樹狀結構
const filterTree = (nodes, searchTerm, expandedIds) => {
  if (!searchTerm || !nodes) return nodes;

  const lowerSearch = searchTerm.toLowerCase();

  return nodes.reduce((filtered, node) => {
    // 檢查當前節點是否符合搜尋字串
    const nodeLabel = node.label || "";
    const isMatch = nodeLabel.toLowerCase().includes(lowerSearch);

    let filteredChildren;
    if (node.children) {
      // 遞迴檢查子節點
      filteredChildren = filterTree(node.children, searchTerm, expandedIds);
    }

    const hasMatchingChild = filteredChildren && filteredChildren.length > 0;

    // 保留條件：當前節點符合，或是底下有任何子節點符合
    if (isMatch || hasMatchingChild) {
      // 如果是因為子節點符合而被保留，該父節點必須被展開，以顯示搜尋結果
      if (hasMatchingChild) {
        expandedIds.push(node.id);
      }

      filtered.push({
        ...node,
        // 如果是父節點名稱命中，但子節點都沒命中，我們預設保留所有子節點供使用者查看
        // 否則，只替換成被過濾後有命中的子節點
        children:
          isMatch && !hasMatchingChild ? node.children : filteredChildren,
      });
    }

    return filtered;
  }, []);
};

export default function CustomTreeSelect({
  companyList = [],
  roleId,
  selectedIds = [], // 從父層接收目前的選擇狀態
  onChange, // 從父層接收更新狀態的函式
  fullWidth = false, // 預設不強制滿版，交給父層決定
  sx = {}, // 接收外部傳入的 sx (用來設定 width 等)
  dropdownMaxHeight = 250, // 下拉選單的最大高度，預設 250px
  defaultSelectAll = false,
  placeholder = null,
  variant = "default",
  showBranchActions = true,
  selectionBehavior = "independent",
  clearOnRoleChange = true,
  menuZIndex,
}) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  // const [selectedIds, setSelectedIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  // 新增狀態：用來控制節點的展開/收合
  const [expandedItems, setExpandedItems] = useState([]);
  const isDashboardTree = variant === "dashboard";
  const selectRef = useRef(null);
  const menuPaperRef = useRef(null);

  //建立一個 Ref 當作防呆開關，確保「預選全部」只發生在第一次載入
  const hasAutoSelected = useRef(false);

  // 確保 roleId effect 跳過首次 mount，只在 roleId 真正變化時才清空選項
  const hasRoleIdMounted = useRef(false);

  // 處理 Select 開關
  const handleClose = () => {
    setOpen(false);
    // 關閉時可選擇清空搜尋，提升下次打開的體驗
    setSearchValue("");
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsidePointerDown = (event) => {
      const target = event.target;
      if (
        selectRef.current?.contains(target) ||
        menuPaperRef.current?.contains(target)
      ) {
        return;
      }

      setOpen(false);
      setSearchValue("");
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handleOutsidePointerDown,
        true,
      );
    };
  }, [open]);

  const handleStopPropagation = (e) => {
    e.stopPropagation();
  };

  // 處理手動展開/收合節點
  const handleExpandedItemsChange = (event, itemIds) => {
    setExpandedItems(itemIds);
  };

  // 新增：處理清空選擇的邏輯
  const handleClearSelection = (e) => {
    e.stopPropagation(); // 避免點擊清除按鈕時觸發下拉選單的開關
    if (onChange) onChange([]); // 呼叫父層的方法來清空
  };

  //TreeNodeChange 的邏輯
  //  抽出共用邏輯
  const diffSelection = (selectedIds, newSelectedIds) => ({
    // 比較舊的 selectedIds 與新的 newSelectedIds，找出是哪個節點被「新增」或「移除」
    addedIds: newSelectedIds.filter((id) => !selectedIds.includes(id)),
    removedIds: selectedIds.filter((id) => !newSelectedIds.includes(id)),
  });

  const handleAdminTreeChange = (event, newSelectedIds) => {
    // 過濾掉 readonly 節點，不讓它真正被選取
    const filteredNewSelectedIds = newSelectedIds.filter(
      (id) => isSelectableCompanyNode(nodeMap[id]),
    );

    const { addedIds, removedIds } = diffSelection(
      validSelectedIds,
      filteredNewSelectedIds,
    );

    let updatedSelection = [...validSelectedIds];

    if (addedIds.length > 0) {
      // 勾選時，自動選擇所有上層與下層
      let idsToAdd = new Set(updatedSelection);

      addedIds.forEach((id) => {
        if (!isSelectableCompanyNode(nodeMap[id])) return;
        idsToAdd.add(id);
        // 找上層時，略過 readonly 的節點
        getAncestors(id, nodeMap).forEach((ancestorId) => {
          if (isSelectableCompanyNode(nodeMap[ancestorId])) {
            idsToAdd.add(ancestorId);
          }
        });

        // 找下層時，也略過 readonly 的節點
        getDescendants(id, nodeMap).forEach((descendantId) => {
          if (isSelectableCompanyNode(nodeMap[descendantId])) {
            idsToAdd.add(descendantId);
          }
        });
      });

      updatedSelection = Array.from(idsToAdd);
    } else if (removedIds.length > 0) {
      // 取消勾選時，只針對單一公司/廠區，不連動其他階層
      updatedSelection = updatedSelection.filter(
        (id) => !removedIds.includes(id),
      );
    }
    // 呼叫父層的方法來更新
    if (onChange) {
      onChange(updatedSelection);
    }
  };

  const handleOperatorTreeChange = (event, newSelectedIds) => {
    const selectableIds = newSelectedIds.filter((id) =>
      isSelectableCompanyNode(nodeMap[id]),
    );
    const { addedIds, removedIds } = diffSelection(
      validSelectedIds,
      selectableIds,
    );
    let updatedSelection = [...validSelectedIds];

    if (addedIds.length > 0) {
      if (validSelectedIds.length > 0) {
        updatedSelection = [];
      }
      // 勾選時，自動選擇所有上層與下層
      // let idsToAdd = new Set(updatedSelection);

      // addedIds.forEach((id) => {
      //   idsToAdd.add(id);
      //   getAncestors(id, nodeMap).forEach((ancestorId) =>
      //     idsToAdd.add(ancestorId),
      //   );
      // });
      addedIds.forEach((id) => {
        // 只加入被點擊的節點本身，不加祖先
        updatedSelection.push(id);
      });

      // 去重
      updatedSelection = [...new Set(updatedSelection)];

      // updatedSelection = Array.from(idsToAdd);
    } else if (removedIds.length > 0) {
      // 取消勾選時，連同所有上層一起移除
      const idsToRemove = new Set();
      removedIds.forEach((id) => {
        idsToRemove.add(id);
        getAncestors(id, nodeMap).forEach((ancestorId) =>
          idsToRemove.add(ancestorId),
        );
      });

      updatedSelection = updatedSelection.filter((id) => !idsToRemove.has(id));
    }
    // 呼叫父層的方法來更新
    if (onChange) {
      onChange(updatedSelection);
    }
  };

  // 動態建立 nodeMap：只要 companyList 改變，就重新建立查找表
  const { treeData, nodeMap, allNodeIds } = useMemo(() => {
    const { tree, map } = buildTreeAndMap(companyList);
    return { treeData: tree, nodeMap: map, allNodeIds: Object.keys(map) }; // ✅ 修正解構名稱對應
  }, [companyList]);

  const validSelectedIds = selectedIds.filter((id) =>
    isSelectableCompanyNode(nodeMap[id]),
  );

  const defaultExpandedItems = useMemo(
    () =>
      treeData
        .filter((node) => Array.isArray(node.children) && node.children.length)
        .map((node) => node.id),
    [treeData],
  );

  const handleOpen = () => {
    if (selectedIds.length !== validSelectedIds.length && onChange) {
      onChange(validSelectedIds);
    }
    setExpandedItems(defaultExpandedItems);
    setOpen(true);
  };

  const getSelectableIdsForNode = (id) => {
    return [id, ...getDescendants(id, nodeMap)].filter(
      (nodeId) => isSelectableCompanyNode(nodeMap[nodeId]),
    );
  };

  const getNodeLabelColor = (node) => {
    const type = String(node?.type || "");
    if (type === "Parent") return "#172b67";
    if (node?.orgType === "COMPANY" || type.includes("Company")) {
      return "#e6700f";
    }
    return "#0f2b5c";
  };

  const isBranchFullySelected = (node) => {
    const selectableIds = getSelectableIdsForNode(node.id);
    return (
      selectableIds.length > 0 &&
      selectableIds.every((id) => validSelectedIds.includes(id))
    );
  };

  const toggleExpandedItem = (event, id) => {
    event.stopPropagation();
    setExpandedItems((current) =>
      current.includes(id)
        ? current.filter((itemId) => itemId !== id)
        : [...current, id],
    );
  };

  const handleDashboardNodeToggle = (event, node) => {
    event.stopPropagation();
    if (!isSelectableCompanyNode(node)) return;

    if (selectionBehavior === "operatorPath") {
      const isSelected = validSelectedIds.includes(node.id);
      const hasSelectedDescendant = getDescendants(node.id, nodeMap).some(
        (id) => validSelectedIds.includes(id),
      );

      if (isSelected && hasSelectedDescendant) {
        return;
      }

      const pathIds = [node.id, ...getAncestors(node.id, nodeMap)];

      if (onChange) {
        onChange(isSelected ? [] : pathIds);
      }
      return;
    }

    if (selectionBehavior === "adminCascade") {
      const updatedSelection = new Set(validSelectedIds);

      if (updatedSelection.has(node.id)) {
        updatedSelection.delete(node.id);
      } else {
        [node.id, ...getAncestors(node.id, nodeMap)]
          .filter((id) => isSelectableCompanyNode(nodeMap[id]))
          .forEach((id) => updatedSelection.add(id));
        getDescendants(node.id, nodeMap)
          .filter((id) => isSelectableCompanyNode(nodeMap[id]))
          .forEach((id) => updatedSelection.add(id));
      }

      if (onChange) {
        onChange(Array.from(updatedSelection));
      }
      return;
    }

    if (roleId === "4") {
      if (onChange) {
        onChange(validSelectedIds.includes(node.id) ? [] : [node.id]);
      }
      return;
    }

    const updatedSelection = new Set(validSelectedIds);

    if (updatedSelection.has(node.id)) {
      updatedSelection.delete(node.id);
    } else {
      updatedSelection.add(node.id);
    }

    if (onChange) {
      onChange(Array.from(updatedSelection));
    }
  };

  const handleDashboardBranchToggle = (event, node) => {
    event.stopPropagation();
    if (!isSelectableCompanyNode(node)) return;

    if (roleId === "4") {
      if (onChange) {
        onChange(validSelectedIds.includes(node.id) ? [] : [node.id]);
      }
      return;
    }

    const selectableIds = getSelectableIdsForNode(node.id);
    const shouldRemove = selectableIds.every((id) =>
      validSelectedIds.includes(id),
    );
    const updatedSelection = new Set(validSelectedIds);

    if (shouldRemove) {
      selectableIds.forEach((id) => updatedSelection.delete(id));
    } else {
      selectableIds.forEach((id) => updatedSelection.add(id));
    }

    if (onChange) {
      onChange(Array.from(updatedSelection));
    }
  };

  const renderDashboardRows = (nodes, level = 0) => {
    return nodes.flatMap((node) => {
      const hasChildren =
        Array.isArray(node.children) && node.children.length > 0;
      const expanded = expandedItems.includes(node.id);
      const checked = validSelectedIds.includes(node.id);
      const hasSelectedBranchItem = getSelectableIdsForNode(node.id).some(
        (id) => validSelectedIds.includes(id),
      );
      const isParentNode = String(node?.type || "") === "Parent";
      const NodeIcon = isParentNode
        ? CorporateFareOutlinedIcon
        : BusinessOutlinedIcon;
      const row = (
        <Box
          key={node.id}
          sx={{
            minHeight: 36,
            px: 1,
            py: 0.5,
            pl: `${12 + level * 20}px`,
            display: "grid",
            gridTemplateColumns: showBranchActions
              ? "22px 54px minmax(0, 1fr) auto 34px"
              : "22px 30px minmax(0, 1fr) 34px",
            alignItems: "center",
            gap: 0.75,
            borderBottom: "1px solid #edf1f7",
            bgcolor: hasSelectedBranchItem ? "#eef6ff" : "transparent",
            "&:hover": { bgcolor: "#f6f9ff" },
          }}
        >
          <Button
            size="small"
            disabled={!hasChildren}
            onClick={(event) => toggleExpandedItem(event, node.id)}
            onMouseDown={handleStopPropagation}
            sx={{
              minWidth: 22,
              width: 22,
              height: 22,
              p: 0,
              borderRadius: "50%",
              color: "#385179",
              bgcolor: hasChildren ? "#fff" : "transparent",
              border: hasChildren
                ? "1px solid #ccd7e6"
                : "1px solid transparent",
              fontSize: 12,
              lineHeight: 1,
              "&:hover": { bgcolor: hasChildren ? "#f0f5ff" : "transparent" },
              "&.Mui-disabled": {
                bgcolor: "transparent",
                borderColor: "transparent",
              },
            }}
          >
            {hasChildren ? (
              expanded ? (
                <ExpandMoreRoundedIcon sx={{ fontSize: 17 }} />
              ) : (
                <ChevronRightRoundedIcon sx={{ fontSize: 17 }} />
              )
            ) : null}
          </Button>

          <Box
            sx={{
              width: 24,
              height: 22,
              borderRadius: "5px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: isParentNode ? "#173875" : "#207540",
              bgcolor: isParentNode ? "#e7efff" : "#def4e6",
              border: "1px solid rgba(24, 77, 150, 0.12)",
            }}
          >
            <NodeIcon sx={{ fontSize: 16 }} />
          </Box>

          <Typography
            sx={{
              minWidth: 0,
              fontSize: 14,
              fontWeight: node.type === "Parent" ? 700 : 600,
              color: getNodeLabelColor(node),
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={node.label}
          >
            <CompanyStatusLabel company={node} name={node.label} />
          </Typography>

          {showBranchActions ? (
            hasChildren ? (
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => handleDashboardBranchToggle(event, node)}
                onMouseDown={handleStopPropagation}
                sx={{
                  minWidth: 82,
                  height: 28,
                  px: 1,
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#173875",
                  borderColor: "#c9d7ff",
                  bgcolor: "#f5f8ff",
                  "&:hover": {
                    borderColor: "#9fb9ff",
                    bgcolor: "#edf4ff",
                  },
                }}
              >
                {isBranchFullySelected(node)
                  ? t("Unselect All")
                  : t("Select All")}
              </Button>
            ) : (
              <Box />
            )
          ) : null}

          <Checkbox
            size="small"
            checked={checked}
            disabled={!isSelectableCompanyNode(node)}
            onChange={(event) => handleDashboardNodeToggle(event, node)}
            onClick={handleStopPropagation}
            sx={{
              p: 0.5,
              color: "#9aa9bd",
              "&.Mui-checked": { color: "#1f74cf" },
            }}
          />
        </Box>
      );

      if (!hasChildren || !expanded) {
        return [row];
      }

      return [row, ...renderDashboardRows(node.children, level + 1)];
    });
  };

  // 當 searchValue 改變時，動態計算過濾後的樹狀資料與需要展開的節點 ID
  const { filteredTree, searchExpandedIds } = useMemo(() => {
    if (!searchValue) {
      return { filteredTree: treeData, searchExpandedIds: [] };
    }
    const idsToExpand = [];
    const tree = filterTree(treeData, searchValue, idsToExpand);
    return { filteredTree: tree, searchExpandedIds: idsToExpand };
  }, [searchValue, treeData]);

  // 同步搜尋結果的展開狀態
  useEffect(() => {
    if (searchValue) {
      setExpandedItems(searchExpandedIds);
    } else {
      setExpandedItems(defaultExpandedItems);
    }
  }, [defaultExpandedItems, searchValue, searchExpandedIds]);

  useEffect(() => {
    // 條件：開啟了全選 + 已經拿到節點資料 + 還沒有自動全選過 + 父層尚未傳入已選取的值
    if (
      defaultSelectAll &&
      allNodeIds.length > 0 &&
      !hasAutoSelected.current &&
      validSelectedIds.length === 0
    ) {
      // 關閉開關，代表已經執行過了，下次不管 selectedIds 怎麼變都不會再自動全選
      hasAutoSelected.current = true;

      // 篩選出所有「非 readonly」的合法節點 ID
      const allSelectableIds = allNodeIds.filter(
        (id) => isSelectableCompanyNode(nodeMap[id]),
      );

      // 把這些 ID 一次全部丟給父層
      if (onChange && allSelectableIds.length > 0) {
        onChange(allSelectableIds);
      }
    }
  }, [
    defaultSelectAll,
    allNodeIds,
    nodeMap,
    onChange,
    validSelectedIds.length,
  ]);

  // 當 roleId 變化時，清空已選擇的項目（跳過首次 mount，避免把 store 裡殘留的值誤清）
  useEffect(() => {
    if (!hasRoleIdMounted.current) {
      hasRoleIdMounted.current = true;
      return;
    }
    if (clearOnRoleChange && onChange) {
      onChange([]);
    }
  }, [clearOnRoleChange, roleId]);

  return (
    <Select
      ref={selectRef}
      size="small"
      multiple
      displayEmpty
      fullWidth={fullWidth}
      sx={[
        sx,
        isDashboardTree && {
          "& .MuiOutlinedInput-notchedOutline": {
            top: 0,
            borderColor: "#cfd6df",
            borderWidth: "1px",
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            display: "none",
          },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            height: "100%",
            paddingTop: 0,
            paddingBottom: 0,
          },
          "& .MuiSelect-icon": {
            top: "50%",
            transform: "translateY(-50%)",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#cfd6df",
          },
          "&.Mui-focused": {
            outline: "none",
            boxShadow: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#cfd6df",
            borderWidth: "1px",
          },
        },
      ]}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={validSelectedIds}
      renderValue={(selected) => {
        if (selected.length === 0) {
          return (
            <Typography color="text.secondary">
              {placeholder ? t(placeholder) : t("Please select...")}
            </Typography>
          );
        }
        const selectedDisplayCount =
          selectionBehavior === "operatorPath"
            ? selected.filter(
                (id) =>
                  !getDescendants(id, nodeMap).some((descendantId) =>
                    selected.includes(descendantId),
                  ),
              ).length
            : selected.filter(
                (id) => isSelectableCompanyNode(nodeMap[id]),
              ).length;
        return t("selectedCount", { count: selectedDisplayCount });
        // return `已選擇 ${selected.length} 個項目`;
      }}
      input={<OutlinedInput />}
      // slotProps={{
      //   icon: { sx: { color: "#4caf50" } },
      // }}
      // endAdornment={
      //   selectedIds.length > 0 && (
      //     <InputAdornment
      //       position="end"
      //       sx={{
      //         position: "absolute",
      //         right: 35, // 往左推移，避免擋到原本 Select 的下拉箭頭
      //       }}
      //     >
      //        <IconButton
      //         aria-label="clear selection"
      //         onClick={handleClearSelection}
      //         edge="end"
      //         size="small"
      //         onMouseDown={handleStopPropagation} // 避免 focus 狀態的跳動
      //         sx={{
      //           cursor: "pointer", // 強制顯示指標游標
      //           pointerEvents: "auto", // 確保可以獨立接收滑鼠事件
      //           zIndex: 3, // 提升層級，避免被 Select 遮罩擋住
      //         }}
      //       >
      //         <ClearIcon fontSize="small" color="primary" />
      //       </IconButton>
      //     </InputAdornment>
      //   )
      // }
      // IconComponent={undefined} // 確保下拉選單的 Icon 不會因為我們加了 endAdornment 而跑版
      MenuProps={{
        sx: menuZIndex ? { zIndex: menuZIndex } : undefined,
        autoFocus: false,
        anchorOrigin: isDashboardTree
          ? { vertical: "bottom", horizontal: "left" }
          : undefined,
        transformOrigin: isDashboardTree
          ? { vertical: "top", horizontal: "left" }
          : undefined,
        PaperProps: {
          ref: menuPaperRef,
          sx: {
            maxHeight: isDashboardTree ? "none" : dropdownMaxHeight,
            width: isDashboardTree ? 520 : "auto",
            display: "flex",
            flexDirection: "column",
            borderRadius: isDashboardTree ? "8px" : undefined,
            border: isDashboardTree ? "1px solid #d8e1ef" : undefined,
            boxShadow: isDashboardTree
              ? "0 8px 24px rgba(22, 34, 65, 0.18)"
              : undefined,
            overflow: "hidden",
          },
        },
      }}
    >
      {/* 搜尋欄區塊 */}
      <Box
        sx={{
          p: 1,
          position: "sticky",
          top: 0,
          bgcolor: "background.paper",
          zIndex: 1,
          display: "flex",
          gap: 1,
          flexWrap: isDashboardTree ? "nowrap" : "wrap",
          alignItems: isDashboardTree ? "center" : undefined,
          borderBottom: isDashboardTree ? "1px solid #e5ebf3" : undefined,
        }}
        onClick={handleStopPropagation}
        onKeyDown={handleStopPropagation}
      >
        <TextField
          // size="small"
          placeholder={t("Search...")}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          sx={{
            flex: 1,
            minWidth: "120px",
            height: isDashboardTree ? 34 : undefined,
            alignSelf: isDashboardTree ? "center" : undefined,
            "& .MuiOutlinedInput-root": {
              borderRadius: isDashboardTree ? "4px" : undefined,
              bgcolor: "#fff",
              height: isDashboardTree ? 34 : undefined,
              minHeight: isDashboardTree ? 34 : undefined,
              maxHeight: isDashboardTree ? 34 : undefined,
              boxSizing: isDashboardTree ? "border-box" : undefined,
              alignItems: isDashboardTree ? "center" : undefined,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              top: 0,
              borderColor: isDashboardTree ? "#cfd6df" : undefined,
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDashboardTree ? "#cfd6df" : undefined,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderColor: isDashboardTree ? "#cfd6df" : undefined,
                borderWidth: isDashboardTree ? "1px" : undefined,
              },
            "& .MuiOutlinedInput-input": {
              height: isDashboardTree ? 34 : undefined,
              boxSizing: isDashboardTree ? "border-box" : undefined,
              py: isDashboardTree ? 0 : undefined,
              px: isDashboardTree ? 1.5 : undefined,
              lineHeight: isDashboardTree ? "34px" : undefined,
            },
            "& .MuiInputAdornment-root": {
              height: isDashboardTree ? 34 : undefined,
              maxHeight: isDashboardTree ? 34 : undefined,
              alignItems: isDashboardTree ? "center" : undefined,
              ml: isDashboardTree ? 0 : undefined,
            },
          }}
          InputProps={{
            sx: { alignItems: "center", height: isDashboardTree ? 34 : 30 },
            endAdornment: searchValue && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("Clear")}
                  onClick={() => setSearchValue("")}
                  onMouseDown={handleStopPropagation}
                  sx={{
                    width: 28,
                    height: 28,
                    p: 0.5,
                    color: isDashboardTree ? "#64748b" : "primary.main",
                    alignSelf: "center",
                    "&:hover": {
                      bgcolor: isDashboardTree ? "#f1f5f9" : undefined,
                    },
                  }}
                >
                  <ClearIcon sx={{ fontSize: 19 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          onClick={handleClearSelection}
          size="small"
          variant="contained"
          onMouseDown={handleStopPropagation} // 避免 focus 狀態的跳動
          sx={{
            height: isDashboardTree ? 34 : "30px",
            minHeight: isDashboardTree ? 34 : undefined,
            boxSizing: isDashboardTree ? "border-box" : undefined,
            whiteSpace: "nowrap",
            flexShrink: 0,
            borderRadius: isDashboardTree ? "4px" : undefined,
            bgcolor: isDashboardTree ? "#1f74cf" : undefined,
            boxShadow: isDashboardTree ? "none" : undefined,
            textTransform: isDashboardTree ? "uppercase" : undefined,
            fontSize: isDashboardTree ? 12 : undefined,
            fontWeight: isDashboardTree ? 700 : undefined,
            "&:hover": {
              bgcolor: isDashboardTree ? "#185ca4" : undefined,
              boxShadow: isDashboardTree ? "none" : undefined,
            },
          }}
        >
          {/* <ClearIcon fontSize="small"  /> */}
          {t("Clear All")}
        </Button>
        {/* 先隱藏
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          mt: 1,
            px: 0.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            自動連動組織階層中所有上層的關係
          </Typography>
          <Switch defaultChecked size="small" />
        </Box> */}
      </Box>

      {/* 樹狀結構區塊 */}
      <Box
        sx={{
          p: isDashboardTree ? 0 : 1,
          flexGrow: 1,
          overflowY: "auto",
          maxHeight: isDashboardTree ? dropdownMaxHeight : undefined,
        }}
        onClick={handleStopPropagation}
        onKeyDown={handleStopPropagation}
      >
        {filteredTree.length > 0 ? (
          isDashboardTree ? (
            renderDashboardRows(filteredTree)
          ) : (
            <RichTreeView
              multiSelect
              checkboxSelection
              items={filteredTree}
              isItemSelectionDisabled={(item) =>
                !isSelectableCompanyNode(item)
              }
              selectedItems={validSelectedIds}
              onSelectedItemsChange={
                roleId === "4"
                  ? handleOperatorTreeChange
                  : handleAdminTreeChange
              }
              // 綁定展開狀態的控制
              expandedItems={expandedItems}
              onExpandedItemsChange={handleExpandedItemsChange}
              slots={{ item: CustomTreeItem }}
              slotProps={{
                item: { nodeMap },
              }}
            />
          )
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            {t("No matching results found")}
          </Typography>
        )}
      </Box>
    </Select>
  );
}
